import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BASE_PATH } from '@crm/api';
import { filter } from 'rxjs';
import { readAccessToken } from '../auth/token-storage';

/**
 * Signale à l'API chaque navigation, pour le rapport mensuel d'utilisation (issue #40).
 *
 * Ce que l'API enregistre volontairement : qu'une page a été consultée, et par qui. Pas laquelle —
 * le rapport compte des consultations, il ne suit personne dans le site. La requête ne porte donc
 * aucune charge utile, et l'adresse consultée ne quitte jamais le navigateur.
 *
 * Écrit avec HttpClient plutôt qu'avec le SDK généré : l'appel n'a ni corps ni réponse, et il est
 * sans conséquence s'il échoue — une page consultée qui n'est pas comptée ne doit rien casser.
 */
@Injectable({ providedIn: 'root' })
export class PageViewTracker {
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly basePath = inject(BASE_PATH);

  /** La page de connexion n'est pas une consultation, et personne n'y est encore authentifié. */
  private static readonly IGNORED = ['/login', '/reset-password'];

  start(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        filter((event) => !PageViewTracker.isIgnored(event.urlAfterRedirects)),
        // Une consultation ne se compte que pour quelqu'un : sans jeton, l'API répondrait 401 et
        // l'intercepteur tenterait un renouvellement pour rien.
        filter(() => readAccessToken() !== null)
      )
      .subscribe(() => this.report());
  }

  private report(): void {
    this.http
      .post(`${this.basePath}/telemetry/page-views`, null, { responseType: 'text' })
      .subscribe({
        error: () => {
          // Silence voulu : la télémétrie ne doit jamais se manifester à l'utilisateur.
        },
      });
  }

  private static isIgnored(url: string): boolean {
    const path = url.split('?')[0];

    return PageViewTracker.IGNORED.some(
      (ignored) => path === ignored || path.startsWith(`${ignored}/`)
    );
  }
}
